import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

import type { UseItemSheetRouteReturn } from '@/domains/pjazza/composables/use-item-sheet-route/useItemSheetRoute.types.ts'

const ITEM_QUERY_PARAM = 'item'

const readItemSlugFromLocation = (): string | null => new URLSearchParams(window.location.search).get(ITEM_QUERY_PARAM)

/**
 * Syncs the `?item={slug}` query param with the item-detail sheet's open state,
 * so a dish can be linked to and shared.
 *
 * Both directions rewrite the current history entry rather than pushing a new
 * one. Pushing looked better — the browser back button closed the sheet — but
 * closing then meant history.back(), and Inertia's own popstate handler
 * responds to that by calling setPage with preserveState: false. That remounts
 * the page component, which rebuilds the entire menu and re-requests every
 * image, and the layout visibly settled twice on every close.
 *
 * The trade is deliberate and worth stating: the browser back button now leaves
 * the venue instead of closing the sheet. The sheet has a close button and a
 * tappable scrim, and neither of those should cost a full page rebuild.
 *
 * @example
 * const { activeItemSlug, isSheetOpen, openItem, closeItem } = useItemSheetRoute()
 */
const useItemSheetRoute = (): UseItemSheetRouteReturn => {
  const activeItemSlug = ref<string | null>(readItemSlugFromLocation())

  const isSheetOpen = computed(() => activeItemSlug.value !== null)

  const buildItemUrl = (slug: string | null): string => {
    const url = new URL(window.location.href)

    if (slug === null) {
      url.searchParams.delete(ITEM_QUERY_PARAM)
    } else {
      url.searchParams.set(ITEM_QUERY_PARAM, slug)
    }

    return `${url.pathname}${url.search}${url.hash}`
  }

  // A real navigation still has to be honoured — forward, back, or a link into
  // a different item on this page.
  const handlePopstate = () => {
    activeItemSlug.value = readItemSlugFromLocation()
  }

  // Inertia's own state is carried across so it still recognises the entry as
  // one of its pages; only the query string changes.
  const openItem = (slug: string) => {
    if (activeItemSlug.value === slug) return

    window.history.replaceState(window.history.state, '', buildItemUrl(slug))
    activeItemSlug.value = slug
  }

  const closeItem = () => {
    if (activeItemSlug.value === null) return

    window.history.replaceState(window.history.state, '', buildItemUrl(null))
    activeItemSlug.value = null
  }

  onMounted(() => window.addEventListener('popstate', handlePopstate))
  onBeforeUnmount(() => window.removeEventListener('popstate', handlePopstate))

  return {
    activeItemSlug,
    isSheetOpen,
    openItem,
    closeItem
  }
}

export { useItemSheetRoute }
